import React, { useCallback, useState } from 'react';

import ErrorIcon from '@mui/icons-material/Error';
import { Handle, Position } from '@xyflow/react';

import { AllowedTaskFunctions } from '../../config/allowed-task-types';
import { ICON_MAPPER } from '../../config/icon-mapper';
import { useNodeConstraints } from '../../context/node-constraints-context';
import { useNodeContext } from '../../context/node-context';
import { useCustomReactFlowState } from '../../context/reactflow-context';
import { getNodeStyle, nodeStyles } from '../../nodes/styles';
import { transformNodeDataToFormData } from '../../nodes/transformer';
import { nodeUtils } from '../../utils/nodeUtils';
import { NodeForm } from '../form/node-form';

export default function CommonNode({
  FormComponent,
  schemaResolver,
  handleConfig = [
    { type: 'source', position: Position.Right },
    { type: 'target', position: Position.Left },
  ],
}) {
  const [showModal, setShowModal] = useState(false);
  const { hasNodeError } = useCustomReactFlowState();
  const { node } = useNodeContext();
  const { data, id, type, metaData } = node;
  const Icon = ICON_MAPPER[type];
  const isError = hasNodeError(id);
  const nodeStyle = getNodeStyle(type, isError, !!Icon);
  const { constraints } = useNodeConstraints();
  const onNodeClick = useCallback((event) => {
    event.stopPropagation();
    setShowModal(true);
  }, []);
  const nodeLabel =
    metaData?.forceDefaultLabel || false
      ? metaData?.defaultLabel
      : nodeUtils.getNodeName(data?.name) || 'Default Name';
  const acceptsIncomingConnections = constraints[type]
    ? constraints[type]?.acceptedParentNodes.length > 0
    : true;
  const acceptsOutgoingConnections = constraints[type]
    ? constraints[type]?.acceptedChildNodes.length > 0
    : true;

  // if the type has a suffix of the function for the task, remove it
  let trimmedType = type;
  AllowedTaskFunctions.forEach((functionName) => {
    if (type.endsWith(functionName)) {
      trimmedType = type.replace(functionName, '').trim();
    }
  });
  const transformedData = { ...transformNodeDataToFormData(data), type: trimmedType };

  return (
    <div style={nodeStyle} onClick={onNodeClick}>
      {isError && (
        <div style={nodeStyles.errorIcon}>
          <ErrorIcon fontSize="small" />
        </div>
      )}
      {Icon && <img src={Icon} alt={metaData?.altText || nodeLabel} />}
      <div style={nodeStyles.nodeLabel}>{nodeLabel}</div>
      {handleConfig.map((handle, index) => {
        const handleId = `handle-${id}-${index}`;
        if (acceptsOutgoingConnections && handle.type === 'source') {
          return (
            <Handle key={handleId} type={handle.type} position={handle.position} id={handleId} />
          );
        } else if (acceptsIncomingConnections && handle.type === 'target') {
          return (
            <Handle key={handleId} type={handle.type} position={handle.position} id={handleId} />
          );
        }
      })}
      <NodeForm
        defaultValues={transformedData}
        showModal={showModal}
        onHide={() => setShowModal(false)}
        modalTitle={metaData?.modalTitle || `${nodeLabel} Details`}
        FormComponent={FormComponent}
        schemaResolver={schemaResolver}
      />
    </div>
  );
}
